import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useIndexService } from "../services/api";
import { useNotification } from "../hooks/useNotification";
import { useConfirm } from "../components/ui/ConfirmProvider";
import { AiOutlineArrowLeft, AiOutlineSync, AiOutlineCheckCircle, AiOutlineCloseCircle, AiOutlineWarning } from "react-icons/ai";
import { formatNumber } from "../utils/dateUtils";
import { useStopOnSessionExpired } from '../auth/useStopOnSessionExpired';

const FASES = {
    IDLE: "Inactivo",
    INICIANDO: "Iniciando...",
    ELIMINANDO_INDICE: "Eliminando índice...",
    CREANDO_INDICE: "Creando índice...",
    INDEXANDO: "Indexando documentos...",
    COMPLETADO: "Completado",
    ERROR: "Error"
};

const AdminIndexPage = () => {
    const navigate = useNavigate();
    const indexService = useIndexService();
    const notify = useNotification();
    const confirm = useConfirm();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState(false);
    const pollingRef = useRef(null);

    const fetchStatus = useCallback(async () => {
        try {
            const response = await indexService.getReindexStatus();
            const newStatus = response.data;
            setStatus(prev => {
                if (prev && prev.running && !newStatus.running) {
                    if (newStatus.fase === "COMPLETADO") {
                        notify.success("Reindexación completada exitosamente");
                    } else if (newStatus.fase === "ERROR") {
                        notify.error("Error en la reindexación: " + (newStatus.mensaje || ""));
                    }
                }
                return newStatus;
            });
            return newStatus;
        } catch (error) {
            console.error("Error consultando estado:", error);
            return null;
        }
    }, []);

    const startPolling = useCallback(() => {
        if (pollingRef.current) return;
        pollingRef.current = setInterval(async () => {
            const s = await fetchStatus();
            if (s && !s.running) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        }, 2000);
    }, [fetchStatus]);

    const stopPolling = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    }, []);

    // Con la sesion muerta el tick solo produce 401 (#168); si se recupera, el reindexado puede
    // seguir corriendo del lado del servidor, asi que se retoma el seguimiento (#172).
    useStopOnSessionExpired(stopPolling, startPolling);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            const s = await fetchStatus();
            if (s && s.running) {
                startPolling();
            }
            setLoading(false);
        };
        init();
        return () => stopPolling();
    }, []);

    const handleStartReindex = async () => {
        // Preview/ETA: si el último status conocido tiene totalDocumentos, estimar tiempo (~50 docs/s)
        let previewMsg = "¿Estás seguro de reindexar? Se eliminará el índice actual y se recreará desde la base de datos. Durante el proceso las búsquedas no estarán disponibles.";
        if (status?.totalDocumentos && status.totalDocumentos > 0) {
            const total = status.totalDocumentos;
            const etaSecs = Math.ceil(total / 50);
            const etaStr = etaSecs < 60 ? `~${etaSecs} segundos` :
                etaSecs < 3600 ? `~${Math.ceil(etaSecs / 60)} minutos` :
                `~${Math.ceil(etaSecs / 3600)} horas`;
            previewMsg = `Se van a reindexar ${formatNumber(total)} documentos (estimado: ${etaStr}). Durante el proceso las búsquedas no estarán disponibles.`;
        }
        const ok = await confirm({
            title: "Reindexar Elasticsearch",
            message: previewMsg,
            confirmLabel: "Reindexar",
            destructive: true,
        });
        if (!ok) return;
        setStarting(true);
        try {
            const response = await indexService.startReindex();
            if (response.status === 202) {
                setStatus(response.data);
                startPolling();
                notify.info("Reindexación iniciada");
            }
        } catch (error) {
            if (error.response && error.response.status === 409) {
                notify.warning("Ya hay una reindexación en curso");
                setStatus(error.response.data);
                startPolling();
            } else {
                notify.error("Error al iniciar la reindexación");
            }
        } finally {
            setStarting(false);
        }
    };

    const formatDuration = (startTime, endTime) => {
        if (!startTime) return "-";
        const end = endTime || Date.now();
        const seconds = Math.floor((end - startTime) / 1000);
        if (seconds < 60) return `${seconds}s`;
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m ${remainingSeconds}s`;
    };

    if (loading) {
        return <div style={styles.loading}>Cargando...</div>;
    }

    const isRunning = status && status.running;
    const hasResult = status && (status.fase === "COMPLETADO" || status.fase === "ERROR");
    const isError = status && status.fase === "ERROR";

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <button style={styles.backButton} onClick={() => navigate("/")}>
                        <AiOutlineArrowLeft style={styles.backIcon} />
                        Volver
                    </button>
                    <h2 style={styles.title}>Gestión de Índices</h2>
                </div>
            </div>

            <div style={styles.card}>
                <div style={styles.cardHeader}>
                    <h3 style={styles.cardTitle}>Recrear Índice de Elasticsearch</h3>
                    <p style={styles.cardDescription}>
                        Elimina el índice actual y lo recrea desde la base de datos con el mapping actualizado.
                        Todos los documentos se reindexan con sus metadatos y permisos.
                    </p>
                </div>

                <div style={styles.cardBody}>
                    {!isRunning && (
                        <button
                            style={{
                                ...styles.actionButton,
                                ...(starting ? styles.actionButtonDisabled : {})
                            }}
                            onClick={handleStartReindex}
                            disabled={starting}
                        >
                            <AiOutlineSync style={starting ? styles.spinIcon : {}} />
                            {starting ? "Iniciando..." : "Recrear Índice"}
                        </button>
                    )}

                    {(isRunning || hasResult) && status && (
                        <div style={styles.progressSection}>
                            <div style={styles.faseRow}>
                                {isRunning && <AiOutlineSync style={styles.spinIcon} />}
                                {status.fase === "COMPLETADO" && <AiOutlineCheckCircle style={styles.successIcon} />}
                                {status.fase === "ERROR" && <AiOutlineCloseCircle style={styles.errorIcon} />}
                                <span style={styles.faseText}>
                                    {FASES[status.fase] || status.fase}
                                </span>
                            </div>

                            {(status.fase === "INDEXANDO" || hasResult) && (
                                <>
                                    <div style={styles.progressBarContainer}>
                                        <div style={{
                                            ...styles.progressBar,
                                            width: `${status.porcentaje || 0}%`,
                                            backgroundColor: isError ? '#EF4444' : '#00468B'
                                        }} />
                                    </div>
                                    <div style={styles.statsRow}>
                                        <span style={styles.stat}>
                                            {status.porcentaje || 0}%
                                        </span>
                                        <span style={styles.stat}>
                                            {status.documentosIndexados || 0} / {status.totalDocumentos || 0} documentos
                                        </span>
                                        {status.errores > 0 && (
                                            <span style={styles.statError}>
                                                <AiOutlineWarning /> {status.errores} errores
                                            </span>
                                        )}
                                        <span style={styles.stat}>
                                            Tiempo: {formatDuration(status.startTime, status.endTime)}
                                        </span>
                                    </div>
                                </>
                            )}

                            {status.mensaje && hasResult && (
                                <div style={{
                                    ...styles.messageBox,
                                    ...(isError ? styles.messageError : styles.messageSuccess)
                                }}>
                                    {status.mensaje}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const spinKeyframes = `
@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
`;

if (typeof document !== 'undefined') {
    const styleEl = document.getElementById('admin-index-styles') || document.createElement('style');
    styleEl.id = 'admin-index-styles';
    styleEl.textContent = spinKeyframes;
    if (!styleEl.parentNode) document.head.appendChild(styleEl);
}

const styles = {
    container: {
        maxWidth: "900px",
        margin: "0 auto",
        padding: "20px",
    },
    loading: {
        textAlign: "center",
        padding: "40px",
        fontSize: "18px",
        color: "#6B7280",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "24px",
    },
    headerLeft: {
        display: "flex",
        alignItems: "center",
        gap: "16px",
    },
    backButton: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "10px 16px",
        backgroundColor: "#6B7280",
        color: "#FFFFFF",
        border: "none",
        borderRadius: "6px",
        cursor: "pointer",
        gap: "8px",
        fontSize: "14px",
        fontWeight: "600",
        transition: "all 0.2s ease",
    },
    backIcon: {
        fontSize: "16px",
    },
    title: {
        fontSize: "24px",
        fontWeight: "700",
        color: "#111827",
        margin: 0,
    },
    card: {
        backgroundColor: "white",
        borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        overflow: "hidden",
    },
    cardHeader: {
        padding: "24px",
        borderBottom: "1px solid #E5E7EB",
    },
    cardTitle: {
        fontSize: "18px",
        fontWeight: "600",
        color: "#111827",
        margin: "0 0 8px 0",
    },
    cardDescription: {
        fontSize: "14px",
        color: "#6B7280",
        margin: 0,
        lineHeight: "1.5",
    },
    cardBody: {
        padding: "24px",
    },
    actionButton: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "12px 24px",
        backgroundColor: "#00468B",
        color: "white",
        border: "none",
        borderRadius: "6px",
        fontSize: "15px",
        fontWeight: "600",
        cursor: "pointer",
        transition: "all 0.2s ease",
    },
    actionButtonDisabled: {
        backgroundColor: "#9CA3AF",
        cursor: "not-allowed",
    },
    progressSection: {
        display: "flex",
        flexDirection: "column",
        gap: "16px",
    },
    faseRow: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        fontSize: "16px",
        fontWeight: "600",
        color: "#111827",
    },
    faseText: {
        fontSize: "16px",
    },
    spinIcon: {
        animation: "spin 1s linear infinite",
        fontSize: "20px",
    },
    successIcon: {
        fontSize: "20px",
        color: "#10B981",
    },
    errorIcon: {
        fontSize: "20px",
        color: "#EF4444",
    },
    progressBarContainer: {
        width: "100%",
        height: "12px",
        backgroundColor: "#E5E7EB",
        borderRadius: "6px",
        overflow: "hidden",
    },
    progressBar: {
        height: "100%",
        borderRadius: "6px",
        transition: "width 0.5s ease",
    },
    statsRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: "20px",
        fontSize: "14px",
        color: "#6B7280",
    },
    stat: {
        display: "flex",
        alignItems: "center",
        gap: "4px",
    },
    statError: {
        display: "flex",
        alignItems: "center",
        gap: "4px",
        color: "#EF4444",
        fontWeight: "500",
    },
    messageBox: {
        padding: "12px 16px",
        borderRadius: "6px",
        fontSize: "14px",
    },
    messageSuccess: {
        backgroundColor: "#DEF7EC",
        color: "#03543F",
        borderLeft: "4px solid #10B981",
    },
    messageError: {
        backgroundColor: "#FDE8E8",
        color: "#9B1C1C",
        borderLeft: "4px solid #EF4444",
    },
};

export default AdminIndexPage;
